import * as Sentry from "@sentry/browser";
import { useTranslations } from "next-intl";
import * as React from "react";

import { Alert, Link } from "@zapier/design-system";

interface ChildComponentProps {
  /** Clears any errors and re-renders the component tree. This does not retain any state. */
  clearError: () => void;
  error: Error;
  errorInfo: React.ErrorInfo;
}

interface Props {
  children?: React.ReactNode;
  fallback?:
    | React.ReactNode
    | ((props: ChildComponentProps) => React.ReactNode);
}

type State =
  | { error: undefined; errorInfo: undefined }
  | { error: Error; errorInfo: React.ErrorInfo };

export interface DefaultErrorMessageProps {
  reason?: string;
}

/** Shows a generic error message with a link to contact support. */
export function DefaultErrorMessage(props: DefaultErrorMessageProps) {
  const t = useTranslations("error-boundary");

  return (
    <Alert variant="default" status="error">
      {props.reason ?? t("default-reason")}{" "}
      {t.rich("contact-support", {
        link: (chunks: React.ReactNode) => (
          <Link href="https://zapier.com/app/get-help">{chunks}</Link>
        ),
      })}
    </Alert>
  );
}

const initialState: State = {
  error: undefined,
  errorInfo: undefined,
};

/** A component that wraps a component tree and optionally displays an error message if one occurs. */
export class ErrorBoundary extends React.PureComponent<Props, State> {
  state: State = initialState;

  static defaultProps: Partial<Props> = {
    children: null,
  };

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // See https://blog.sentry.io/2017/09/28/react-16-error-boundaries#send-the-errors-to-sentry
    const captureContext = {
      contexts: {
        react: { ...errorInfo },
      },
    };

    Sentry.captureException(error, captureContext);

    this.setState({ error, errorInfo });
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    const fallbackElement =
      typeof this.props.fallback === "function"
        ? this.props.fallback({
            clearError: () => this.setState(initialState),
            error: this.state.error,
            errorInfo: this.state.errorInfo,
          })
        : this.props.fallback;

    if (React.isValidElement(fallbackElement)) {
      return fallbackElement;
    }

    return <DefaultErrorMessage />;
  }
}
